import React from 'react';
import { X } from 'lucide-react';

/**
 * Reusable Modal with built-in scroll support.
 *
 * Props:
 *   isOpen      – boolean
 *   title       – string (header text)
 *   children    – body content
 *   onClose     – close handler
 *   actions     – footer buttons (optional)
 *   maxWidth    – override default 500 px width (optional)
 *   wide        – shorthand for 720 px wide modal (optional)
 */
export function Modal({ isOpen, title, children, onClose, actions, maxWidth, wide }) {
  if (!isOpen) return null;

  const resolvedMaxWidth = maxWidth || (wide ? 720 : 500);

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 40,
        }}
        onClick={onClose}
      />

      {/* Modal container – uses flexbox centering so the dialog can shrink when viewport is small */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,               /* breathing room from viewport edges */
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            pointerEvents: 'auto',
            background: '#ffffff',
            borderRadius: 12,
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
            maxWidth: resolvedMaxWidth,
            width: '90%',
            maxHeight: '90vh',       /* never exceed viewport height */
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Sticky header ── */}
          <div
            style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e4e4e7',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#18181b',
                margin: 0,
              }}
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                color: '#71717a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* ── Scrollable body ── */}
          <div
            style={{
              padding: 24,
              overflowY: 'auto',
              overflowX: 'auto',
              flex: '1 1 auto',
              minHeight: 0,          /* allow flex child to shrink below content size */
            }}
          >
            {children}
          </div>

          {/* ── Sticky footer ── */}
          {actions && (
            <div
              style={{
                padding: 16,
                borderTop: '1px solid #e4e4e7',
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              {actions}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
